import { ContextStore } from "../../../../../../../react-core/Flux/Variables";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { groupBy } from "../../../../../../../react-core/Utils/Arrays";
import { ContextType } from "../../../../../../../Wrap/Constants/Enums/ContextType";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { HierarchyLevelName } from "../../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { AccountRole } from "../../../../../../../Wrap/Models/Domains/Account/AccountRole";
import { AccountStatus } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/AccountStatus";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { InvestorType } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/InvestorType";
import { SubAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/SubAccount";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import { Urls } from "../../../../../Urls";
import { defaultAccountMinDate } from "../../Resource/Content";
import { ProductValuationDetailSummary } from "../../../../../../../Wrap/Models/Domains/Valuation/ProductValuationDetailSummary";

export const getSelectedHeadAccount = (selectedSubAccounts: string[], allAccounts: ClientAccount[]): string[] => {
    return selectedHeadAccounts(selectedSubAccounts, allAccounts).map((account) => account.HierarchyId);
};

export const getSelectedHeadClAccountId = (selectedSubAccounts: string[], allAccounts: ClientAccount[]): string[] => {
    return selectedHeadAccounts(selectedSubAccounts, allAccounts).map((account) => account.HeadClAccountId);
};

// get the minimum inception date of selected head accounts
// in case client account has no known inception date, set it as default
export const getSelectedAccountsInceptionDate = (selectedSubAccounts: string[], allAccounts: ClientAccount[]) => {
    let date: Date = null;
    allAccounts.map((x) => {
        x.SubAccounts.map((s) => {
            const curDate = new Date(s.InceptionDate + "");
            if (selectedSubAccounts?.includes(s.HierarchyId) && curDate.getTime() > defaultAccountMinDate.getTime()) {
                date = !date || curDate.getTime() < date.getTime() ? curDate : date;
            }
        });
    });

    return date ?? defaultAccountMinDate;
};

//gets selected headAccounts based on selected sub account hierarchy ids,
//A head account is selected when one of its sub account is selected
export const selectedHeadAccounts = (selectedSubAccounts: string[], allAccounts: ClientAccount[]): ClientAccount[] => {
    return (
        allAccounts?.filter((account) =>
            (account.SubAccounts || []).some((subAccount) => selectedSubAccounts?.includes(subAccount.HierarchyId))
        ) || []
    );
};

//gets selected subAccounts based on selected sub account hierarchy ids,
export const selectedSubAccount = (selectedSubAccounts: string[], allAccounts: ClientAccount[]): SubAccount[] => {
    const selected = [];
    (allAccounts || []).forEach((clientaccount) => {
        clientaccount.SubAccounts.forEach((subAccount) => {
            if (selectedSubAccounts.includes(subAccount.HierarchyId)) {
                selected.push(subAccount);
            }
        });
    });
    return selected;
};

//gets selected headAccounts based on selected sub account hierarchy ids,
//A head account is selected when all its subaccounts are selected
export const selectedHeadAccountsOnly = (
    selectedSubAccounts: string[],
    allAccounts: ClientAccount[]
): ClientAccount[] => {
    const selectedHeadAccounts = [];
    allAccounts.forEach((account) => {
        const subAccounts = (selectedSubAccounts || []).filter((subAccount) =>
            account.SubAccounts.some((x) => HierarchyIdHelper.Equals(x.HierarchyId, subAccount))
        );
        if (subAccounts.length == account.SubAccounts.length) {
            selectedHeadAccounts.push(account);
        }
    });
    return selectedHeadAccounts;
};

export const getUrl = (clientAccount: ClientAccount, accountRoles: Array<AccountRole>, tab: string): string => {
    // check if investor
    if (clientAccount?.InvestorType == InvestorType.Consolidated) {
        // consolidate account
        return Urls.get()
            .main.PortfolioDashboard({ hierarchyId: clientAccount?.HierarchyId })
            [tab.toLowerCase()].getUrl();
    }
    // normal account
    // get primary customer
    const primaryCustomer = accountRoles?.find((e) => e?.IsPrimary);
    return Urls.get()
        .main.PortfolioDashboard({ hierarchyId: primaryCustomer?.Customer?.HierarchyId })
        [tab.toLowerCase()].getUrl();
};

export const isConsolidate = () => {
    const customerId = ContextStore.getUrlParamsHierarchyId(ContextType.Customer);
    const { hierarchyLevel, entityId } = HierarchyIdHelper.generateHierarchyId(customerId);
    return hierarchyLevel === HierarchyLevelName.Account;
};

// get subaccount info and according head account info by subaccount claccount id
// if give head account claccountid then return null for subaccount
export const getSubAccountInfoByClAccountId = (
    claccountid: string,
    allAccounts: ClientAccount[]
): { headAccount: ClientAccount; subAccount: SubAccount } => {
    if (CommonHelper.isNullOrEmpty(claccountid)) {return null;}
    //head account
    if (!claccountid.includes("-")) {
        return {
            headAccount: (allAccounts || []).find((ele) => ele.HeadClAccountId === claccountid),
            subAccount: null,
        };
    }
    const headAccount = (allAccounts || []).find((head) =>
        (head.SubAccounts || []).some((sub) => sub.ClAccountId === claccountid)
    );
    //allAccounts only contains active sub-accounts,for claccountid of inactive sub-accounts cant get headAccount from allAccounts
    if (headAccount === null || headAccount === undefined) {
        return {
            headAccount: (allAccounts || []).find((ele) => ele.HeadClAccountId === claccountid.split("-")[0]),
            subAccount: null,
        };
    }
    const subAccount = (headAccount?.SubAccounts || []).find((ele) => ele.ClAccountId === claccountid);
    return {
        headAccount: headAccount,
        subAccount: subAccount,
    };
};

//gets selected sub account claccountIds based on selected sub account hierarchy ids
export const getSelectedSubAccountIds = (selectedSubAccounts: string[], allAccounts: ClientAccount[]): string[] => {
    const subAccountIds = [];
    if (!selectedSubAccounts || !allAccounts) {
        return subAccountIds;
    }

    allAccounts.forEach((clientAccount) =>
        clientAccount.SubAccounts.forEach((sa) => {
            if (selectedSubAccounts.some((x) => HierarchyIdHelper.Equals(sa.HierarchyId, x))) {
                subAccountIds.push(sa.ClAccountId);
            }
        })
    );

    return subAccountIds;
};

// group the selected head accounts by account type (Individual / Trust / Joint)
export const headAccountGroupByInvestorType = (
    selectedAccounts: ClientAccount[]
): { Individual: ClientAccount[]; Joint: ClientAccount[]; Trust: ClientAccount[] } => {
    const groups = groupBy("InvestorType", selectedAccounts);
    //combine same type with different enum
    const result = { Individual: [], Joint: [], Trust: [] };
    Object.keys(groups).forEach((ele) => {
        if (ele == InvestorType.Individ.toString() || ele == InvestorType.Individual.toString()) {
            result.Individual = result.Individual.concat(groups[ele] || []);
        }
        if (ele == InvestorType.Joint.toString() || ele == InvestorType.JointIndividual.toString()) {
            result.Joint = result.Joint.concat(groups[ele] || []);
        }
        if (ele == InvestorType.Trust.toString()) {
            result.Trust = result.Trust.concat(groups[ele] || []);
        }
    });
    return result;
};

export const orderHeadAccountGroupByInvestorType = (allAccounts: ClientAccount[]): ClientAccount[] => {
    const groupHeadaccount = headAccountGroupByInvestorType(allAccounts);
    const result = [];
    groupHeadaccount.Individual.forEach((account) => {
        result.push(account);
    });
    groupHeadaccount.Joint.forEach((account) => {
        result.push(account);
    });
    groupHeadaccount.Trust.forEach((account) => {
        result.push(account);
    });
    return result;
};

const putWrapCashAsLastOne = (subAccount: SubAccount[] = []): SubAccount[] => {
    if (!subAccount) {return [];}
    const WrapCash = subAccount.find((ele) => ele.SubAccountType == SubAccountType.WrapCash);
    return subAccount.filter((ele) => ele.SubAccountType != SubAccountType.WrapCash).concat(WrapCash || []);
};

// group the selected sub accounts by account type (Individual / Trust / Joint)
export const SubAccountGroupByInvestorType = (
    selectedAccounts: ClientAccount[],
    selectedSubAccounts: SubAccount[]
): { Individual: SubAccount[]; Joint: SubAccount[]; Trust: SubAccount[] } => {
    const headAccountGroups = headAccountGroupByInvestorType(selectedAccounts);
    const result = { Individual: [], Joint: [], Trust: [] };
    Object.keys(headAccountGroups).forEach((ele) => {
        headAccountGroups[ele].forEach((clientAccount: ClientAccount) => {
            const selected = selectedSubAccounts.filter((subaccount) => clientAccount.SubAccounts.includes(subaccount));
            const subAccounts = putWrapCashAsLastOne(selected);
            result[ele] = result[ele].concat(subAccounts);
        });
    });

    return result;
};

// group the info for summary dashboard
// clAccountKey key to get the claccountid
export const groupProductDetail = (
    selectedAccounts: string[],
    allAccounts: ClientAccount[],
    productDetail: ProductValuationDetailSummary[] = [],
    clAccountKey: string
): { Individual: any[]; Joint: any[]; Trust: any[] } => {
    const result = { Individual: [], Joint: [], Trust: [] };
    if (
        !productDetail ||
        productDetail.length === 0 ||
        !selectedAccounts ||
        selectedAccounts.length == 0 ||
        !allAccounts ||
        allAccounts.length == 0
    )
        {return result;}
    if (!productDetail[0][clAccountKey]) {
        console.error(`can not find ${clAccountKey} in ${typeof productDetail[0]}`);
        return result;
    }

    const groupedSubAccount = SubAccountGroupByInvestorType(
        selectedHeadAccounts(selectedAccounts, allAccounts),
        selectedSubAccount(selectedAccounts, allAccounts)
    );

    Object.keys(groupedSubAccount).forEach((key) => {
        groupedSubAccount[key].forEach((subAccount: SubAccount) => {
            if (!isInActiveAccount(subAccount?.Status)) {
                const item = productDetail.find((ele) => ele[clAccountKey] == subAccount.ClAccountId);
                if (item) {result[key].push(item);}
            }
        });
    });
    return result;
};

export const findHeadAccount = (allAccounts: ClientAccount[], hierarchyId: string): ClientAccount => {
    if (!allAccounts || allAccounts.length == 0) {return null;}
    const { hierarchyLevel } = HierarchyIdHelper.generateHierarchyId(hierarchyId);
    let clientAccount = null;
    if (hierarchyLevel === HierarchyLevelName.SubAccount) {
        clientAccount = allAccounts.find((account) =>
            account.SubAccounts.some((subAccount) => subAccount.HierarchyId === hierarchyId)
        );
    } else if (hierarchyLevel === HierarchyLevelName.Account) {
        clientAccount = allAccounts.find((account) => account.HierarchyId === hierarchyId);
    }
    return clientAccount;
};

// check headaccount is closing status
// default as false
export const isClosingAccount = (allAccounts: ClientAccount[], hierarchyId: string): boolean => {
    if (!allAccounts || allAccounts.length == 0) {return false;}
    const clientAccount = findHeadAccount(allAccounts, hierarchyId);

    if (clientAccount) {
        return [AccountStatus.Closing, AccountStatus.Closed].includes(clientAccount.AccountStatus);
    } else {
        console.error("Wrong hierarchyId level, only accept subaccount or headaccount hierarchy id");
        return false;
    }
};

export const isInActiveAccount = (status: AccountStatus): boolean => {
    return [AccountStatus.InActive, AccountStatus.DELETED].includes(status);
};

export const updateAccountInfo = (newAccountInfo: ClientAccount, allAccounts: ClientAccount[]): ClientAccount[] => {
    if (!newAccountInfo || !allAccounts || allAccounts.length == 0) {return [];}
    const findAccountIndex = allAccounts.findIndex((acc) => acc.HeadClAccountId == newAccountInfo.HeadClAccountId);
    if (findAccountIndex < 0) {
        console.error("Not find new account in all accounts, no update made.");
        return allAccounts;
    }
    allAccounts[findAccountIndex] = newAccountInfo;
    return allAccounts;
};

export const isTradingAccount = (allAccounts: ClientAccount[]): boolean => {
    if (!allAccounts) {return false;}
    return allAccounts.every((acc) => acc.Company == "DIMCO");
};
